import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { register } from '../services/api';

const BAA_TEXT = `BUSINESS ASSOCIATE AGREEMENT

This Business Associate Agreement ("BAA") is entered into between ChartAudit AI ("Business Associate") and the undersigned Covered Entity ("Covered Entity") as of the date of electronic signature below.

1. DEFINITIONS
Terms used in this Agreement shall have the same meaning as those terms defined in the HIPAA Rules (45 CFR Parts 160 and 164).

2. OBLIGATIONS OF BUSINESS ASSOCIATE
Business Associate agrees to:
(a) Not use or disclose Protected Health Information (PHI) other than as permitted or required by this Agreement or as required by law.
(b) Use appropriate administrative, physical, and technical safeguards to prevent unauthorized use or disclosure of PHI, including implementing requirements of the HIPAA Security Rule with regard to electronic PHI (ePHI).
(c) Report to Covered Entity any use or disclosure of PHI not provided for by this Agreement of which it becomes aware, including any security incident or breach of unsecured PHI.
(d) Ensure that any subcontractors or agents who create, receive, maintain, or transmit PHI on behalf of the Business Associate agree to the same restrictions and conditions that apply to the Business Associate.
(e) Make available PHI in accordance with the individual's rights as required under the HIPAA Rules.
(f) Make its internal practices, books, and records relating to the use and disclosure of PHI available to the Secretary of the U.S. Department of Health and Human Services for purposes of determining compliance.
(g) At termination of this Agreement, return or destroy all PHI received from Covered Entity that Business Associate maintains in any form.

3. PERMITTED USES AND DISCLOSURES
Business Associate may use or disclose PHI:
(a) As necessary to perform services outlined in the service agreement, including but not limited to automated chart auditing, quality measure analysis, compliance review, and report generation.
(b) For the proper management and administration of the Business Associate, provided disclosures are required by law or Business Associate obtains reasonable assurance from any third party that the PHI will be held confidentially.
(c) To de-identify PHI in accordance with 45 CFR 164.514(a)-(c).

4. DATA SECURITY
Business Associate shall:
(a) Encrypt all PHI at rest and in transit using industry-standard AES-256 encryption.
(b) Implement access controls, audit logging, and authentication measures.
(c) Conduct periodic risk assessments and remediate identified vulnerabilities.
(d) Maintain incident response procedures for potential breaches.
(e) All uploaded documents are encrypted immediately upon receipt and stored in secure, access-controlled environments.

5. BREACH NOTIFICATION
Business Associate shall notify Covered Entity of any breach of unsecured PHI without unreasonable delay and no later than thirty (30) calendar days after discovery of the breach. Notification shall include the identification of each individual affected and a description of the breach.

6. TERM AND TERMINATION
(a) This Agreement shall remain in effect for the duration of the service relationship between the parties.
(b) Either party may terminate this Agreement if the other party materially breaches the terms of this Agreement and fails to cure the breach within thirty (30) days of written notice.
(c) Upon termination, Business Associate shall return or destroy all PHI in its possession.

7. MISCELLANEOUS
(a) This Agreement shall be governed by applicable federal law, including the HIPAA Rules.
(b) Any ambiguity in this Agreement shall be resolved in favor of a meaning that permits the parties to comply with the HIPAA Rules.
(c) The parties agree that this electronically signed Agreement is legally binding and enforceable.

By typing your full legal name and checking the acknowledgment below, you are electronically signing this Business Associate Agreement under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act) and the Uniform Electronic Transactions Act (UETA).`;

export default function Register() {
  const [form, setForm] = useState({
    agencyName: '', email: '', password: '', confirmPassword: '',
    firstName: '', lastName: '', phone: '', address: '', npiNumber: '', accountType: 'agency', website: '',
    subscriptionPlan: 'pay_per_report',
  });
  const [baaState, setBaaState] = useState({
    showBaa: false,
    scrolledToBottom: false,
    signerName: '',
    signerTitle: '',
    agreed: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1 = account info, 2 = BAA

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleBaaScroll = (e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (atBottom && !baaState.scrolledToBottom) {
      setBaaState({ ...baaState, scrolledToBottom: true });
    }
  };

  const goToStep2 = (e) => {
    e.preventDefault();
    setError('');
    if (!form.agencyName || !form.email || !form.password || !form.confirmPassword || !form.firstName || !form.lastName || !form.phone) {
      return setError('Please fill in all required fields (including phone number)');
    }
    if (form.password !== form.confirmPassword) {
      return setError('Passwords do not match');
    }
    if (form.password.length < 8) {
      return setError('Password must be at least 8 characters');
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!baaState.agreed || !baaState.signerName) {
      return setError('You must read, sign, and agree to the Business Associate Agreement');
    }
    setLoading(true);
    try {
      const res = await register({
        ...form,
        baaSigned: true,
        baaSignerName: baaState.signerName,
        baaSignerTitle: baaState.signerTitle,
        subscriptionPlan: form.subscriptionPlan,
      });
      if (res.data.pendingApproval) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen auth-gradient-bg flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full glass-card rounded-2xl shadow-xl border border-white/60 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Submitted!</h2>
          <p className="text-gray-600 mb-3">
            Your account is pending admin approval. You'll be able to log in once an administrator reviews and approves your registration.
          </p>
          <p className="text-sm text-teal-700 bg-teal-50 rounded-lg px-4 py-2 mb-6">
            Your signed Business Associate Agreement (BAA) has been recorded for HIPAA compliance.
          </p>
          <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium hover:underline">Back to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-gradient-bg flex items-center justify-center px-4 py-8">
      <div className="max-w-lg w-full glass-card rounded-2xl shadow-xl border border-white/60 p-8">
        <div className="text-center mb-6">
          <img
            src="/chart-auditer-ai-logo.svg"
            alt="Chart Auditer AI logo"
            className="h-20 w-auto max-w-full mx-auto mb-3 object-contain"
          />
          <h1 className="text-3xl font-bold text-gray-800">
            <span className="text-teal-600">Chart</span>Audit AI
          </h1>
          <p className="text-gray-500 mt-2">Create your account</p>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === 1 ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 1 ? 'bg-teal-600 text-white' : 'bg-gray-300 text-white'}`}>1</span>
            Account Info
          </div>
          <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${step === 2 ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === 2 ? 'bg-teal-600 text-white' : 'bg-gray-300 text-white'}`}>2</span>
            HIPAA BAA
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>
        )}

        {/* STEP 1: Account Info */}
        {step === 1 && (
          <form onSubmit={goToStep2} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">I am a...</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setForm({ ...form, accountType: 'agency' })}
                  className={`p-3 rounded-xl border-2 text-center transition ${
                    form.accountType === 'agency' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  <p className="font-semibold text-sm">Home Health Agency</p>
                  <p className="text-xs mt-1 text-gray-500">Audit my own charts</p>
                </button>
                <button type="button" onClick={() => setForm({ ...form, accountType: 'consultant' })}
                  className={`p-3 rounded-xl border-2 text-center transition ${
                    form.accountType === 'consultant' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  <p className="font-semibold text-sm">Consultant</p>
                  <p className="text-xs mt-1 text-gray-500">Audit charts for multiple agencies</p>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {form.accountType === 'consultant' ? 'Company / Consulting Firm Name *' : 'Agency Name *'}
              </label>
              <input value={form.agencyName} onChange={update('agencyName')} required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input value={form.firstName} onChange={update('firstName')} required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                <input value={form.lastName} onChange={update('lastName')} required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input type="email" value={form.email} onChange={update('email')} required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <input type="password" value={form.password} onChange={update('password')} required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                <input type="password" value={form.confirmPassword} onChange={update('confirmPassword')} required
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input value={form.phone} onChange={update('phone')} required placeholder="(555) 123-4567"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {form.accountType === 'consultant' ? 'Website' : 'NPI Number'}
                </label>
                {form.accountType === 'consultant' ? (
                  <input value={form.website} onChange={update('website')} placeholder="https://..."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
                ) : (
                  <input value={form.npiNumber} onChange={update('npiNumber')}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
              <input value={form.address} onChange={update('address')} placeholder="123 Main St, City, State ZIP"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white/80" />
            </div>

            {/* Pricing Plan Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Choose Your Plan</label>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setForm({ ...form, subscriptionPlan: 'pay_per_report' })}
                  className={`p-4 rounded-xl border-2 text-left transition relative ${
                    form.subscriptionPlan === 'pay_per_report' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <p className="font-bold text-lg text-gray-800">$50</p>
                  <p className="text-xs font-semibold text-gray-600">per report</p>
                  <p className="text-xs text-gray-500 mt-2">Pay only when you download. No commitment.</p>
                  {form.subscriptionPlan === 'pay_per_report' && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                  )}
                </button>
                <button type="button" onClick={() => setForm({ ...form, subscriptionPlan: 'monthly' })}
                  className={`p-4 rounded-xl border-2 text-left transition relative ${
                    form.subscriptionPlan === 'monthly' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="absolute -top-2.5 left-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">BEST VALUE</div>
                  <p className="font-bold text-lg text-gray-800">$100<span className="text-sm font-normal text-gray-500">/mo</span></p>
                  <p className="text-xs font-semibold text-gray-600">4 reports included</p>
                  <p className="text-xs text-gray-500 mt-2">$40/report after 4. Save up to $60/mo.</p>
                  {form.subscriptionPlan === 'monthly' && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                  )}
                </button>
              </div>
            </div>

            <button type="submit"
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold py-2.5 rounded-xl shadow-md hover:shadow-lg">
              Continue to BAA Agreement
            </button>
          </form>
        )}

        {/* STEP 2: BAA Agreement */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <button type="button" onClick={() => { setStep(1); setError(''); }}
              className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1 mb-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
              Back to account info
            </button>

            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-amber-800">HIPAA Compliance Required</p>
                  <p className="text-xs text-amber-700 mt-1">
                    As a healthcare organization handling Protected Health Information (PHI), a signed Business Associate Agreement is required before using ChartAudit AI. Please read the agreement in full, then provide your digital signature below.
                  </p>
                </div>
              </div>
            </div>

            {/* BAA Document */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Business Associate Agreement (BAA) v1.0</span>
                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">HIPAA Required</span>
              </div>
              <div
                className="h-64 overflow-y-auto p-4 text-xs text-gray-700 bg-white font-mono whitespace-pre-wrap leading-relaxed"
                onScroll={handleBaaScroll}
              >
                {BAA_TEXT}
              </div>
              {!baaState.scrolledToBottom && (
                <div className="bg-gray-50 px-4 py-2 text-center border-t border-gray-200">
                  <p className="text-xs text-gray-500">Scroll to bottom to continue</p>
                </div>
              )}
            </div>

            {/* Digital Signature Section */}
            <div className={`border rounded-xl p-4 space-y-3 transition ${baaState.scrolledToBottom ? 'border-teal-200 bg-teal-50/30' : 'border-gray-200 bg-gray-50 opacity-60'}`}>
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Digital Signature
              </h3>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Full Legal Name (as your signature) *</label>
                <input
                  value={baaState.signerName}
                  onChange={(e) => setBaaState({ ...baaState, signerName: e.target.value })}
                  placeholder="Type your full legal name"
                  disabled={!baaState.scrolledToBottom}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                  style={baaState.signerName ? { fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: '1.25rem' } : {}}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title / Role (optional)</label>
                <input
                  value={baaState.signerTitle}
                  onChange={(e) => setBaaState({ ...baaState, signerTitle: e.target.value })}
                  placeholder="e.g., Administrator, Director of Nursing, Owner"
                  disabled={!baaState.scrolledToBottom}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                baaState.agreed ? 'bg-teal-50 border-teal-300' : 'bg-white border-gray-200 hover:border-gray-300'
              } ${!baaState.scrolledToBottom ? 'opacity-50 pointer-events-none' : ''}`}>
                <input
                  type="checkbox"
                  checked={baaState.agreed}
                  onChange={(e) => setBaaState({ ...baaState, agreed: e.target.checked })}
                  disabled={!baaState.scrolledToBottom}
                  className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-xs text-gray-700 leading-relaxed">
                  I have read and agree to the Business Associate Agreement. I understand that by typing my name above and checking this box, I am providing my legally binding electronic signature under the E-SIGN Act and UETA. I am authorized to sign this agreement on behalf of <strong>{form.agencyName || 'my organization'}</strong>.
                </span>
              </label>
            </div>

            <button type="submit" disabled={loading || !baaState.agreed || !baaState.signerName}
              className="w-full bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold py-2.5 rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? 'Submitting...' : 'Sign BAA & Register'}
            </button>

            <p className="text-xs text-gray-400 text-center">
              Your account will be reviewed and approved by an administrator before you can log in.
            </p>
          </form>
        )}

        <p className="text-center text-gray-500 text-sm mt-6">
          Already have an account? <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
